import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface ExpenseCategoryRow {
  id: string;
  name: string;
  color: string | null;
  visible: boolean;
  _count: { expenses: number };
}

export interface ExpenseRow {
  id: string;
  spentOn: string;
  /** Minor units as strings. BigInt does not survive JSON. */
  costMinor: string;
  chargeMinor: string;
  quantity: number;
  description: string | null;
  billable: boolean;
  exported: boolean;
  project: { id: string; name: string; customerName: string };
  category: { id: string; name: string; color: string | null };
  person: { id: string; name: string };
  invoice: { id: string; number: string } | null;
}

export interface ExpenseList {
  rows: ExpenseRow[];
  totals: { costMinor: string; chargeMinor: string; billableChargeMinor: string };
}

export interface ExpenseInput {
  projectId: string;
  categoryId: string;
  spentOn: string;
  cost: string;
  charge: string;
  quantity?: number;
  description?: string | null;
  billable?: boolean;
  employeeId?: string;
}

export function useExpenses(filter: { from?: string; to?: string; projectId?: string } = {}) {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) if (v) query.set(k, v);
  const suffix = query.toString() ? `?${query}` : '';
  return useQuery({
    queryKey: ['expenses', filter],
    queryFn: () => api.get<ExpenseList>(`/expenses${suffix}`),
  });
}

export function useExpenseCategories() {
  return useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: () => api.get<ExpenseCategoryRow[]>('/expenses/categories'),
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    // An expense changes what an invoice would contain.
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };
}

export function useSaveExpense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: ExpenseInput & { id?: string }) =>
      id ? api.patch<ExpenseRow>(`/expenses/${id}`, body) : api.post<ExpenseRow>('/expenses', body),
    onSuccess: invalidate,
  });
}

export function useDeleteExpense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: string }>(`/expenses/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveExpenseCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { id?: string; name: string; color?: string | null; visible?: boolean }) =>
      api.post<ExpenseCategoryRow>('/expenses/categories', body),
    onSuccess: invalidate,
  });
}
